<script setup lang="ts">
import * as yup from "yup"
import { useOperationStore } from "~/stores/operation.store"
import { cloneDeep } from "lodash-es"
import type { components } from "~/assets/api/openapi"
import {yupTestOnlyOnCreate} from "~/utils/yup-test-only-on-create";
import {isDateValid} from "~/utils/is-date-valid";

enum ActionType {
  KNOWLEDGE_TEST = "KNOWLEDGE_TEST",
  APTITUDE_TEST = "APTITUDE_TEST",
  ADAPTATION_COURSE = "ADAPTATION_COURSE",
}

enum Result {
  NOT_AVAILABLE = "NOT_AVAILABLE",
  PASSED = "PASSED",
  NOT_PASSED = "NOT_PASSED",
  CANCELLED = "CANCELLED",
}

type ApplicationType = "EU" | "THIRD_PARTY"
const operationStore = useOperationStore()
const schema = yup.object().shape({
  action: yup
    .string()
    .required("Pflichtfeld")
    .test(
      "action-validation-based-on-mode",
      "Ungültiger Wert",
      yupTestOnlyOnCreate((value) =>
        Object.values(ActionType).includes(value as ActionType)
      )
    ),

  actionDate: yup
    .string()
    .required("Pflichtfeld")
    .test("is-date-valid", "Ungültiges Datum", (value?: string) => {
      return value ? isDateValid(value) : true
    }),

  result: yup
      .string()
      .required("Pflichtfeld")
      .test(
          "result-validation-based-on-mode",
          "Der Arbeitsschritt kann ohne Ergebnis nicht abgeschlossen werden",
          yupTestOnlyOnCreate((value) =>
              [Result.PASSED, Result.NOT_PASSED, Result.CANCELLED].includes(
                  value as Result
              )
          )
      ),

  resultDate: yup
      .string()
      .required("Pflichtfeld")
      .test("is-date-valid", "Ungültiges Datum", (value?: string) => {
        return value ? isDateValid(value) : true
      }),
})

type Schema = yup.InferType<typeof schema>

const currentWorkStepData = computed<Schema>(
  () => (operationStore.currentWorkStep?.data ?? {}) as unknown as Schema
)

const state = reactive<Schema>(cloneDeep(currentWorkStepData.value))
const applicationType = computed<ApplicationType | undefined>(() => {
  const textKey = operationStore.findFormDataValueByTextKey<
    components["schemas"]["LocaleValue"]
  >("gsb.pug.applicationDataFields.applicationType")?.textKey

  const lastPart = textKey?.split(".").pop()
  return lastPart as ApplicationType | undefined
})

const isApplicationTypeEU = computed<boolean>(
  () => applicationType.value === "EU"
)
</script>

<template>
  <IffOperationWorkStepFormV2 :state="state" :schema="schema">
    <template #above-form>
      <IffReviewsSection
        v-if="operationStore.currentWorkStep"
        :work-step-id="operationStore.currentWorkStep?.id"
        additional-headline="Anpassungsmaßnahme"
      />
    </template>

    <IffContentSection
      headline="Entscheidung der antragstellenden Person"
      :is-last-child="false"
    >
      <IffDualRadioGroup
        name="action"
        v-model="state.action"
        legend="Maßnahme"
        required
        :options="[
          {
            label: isApplicationTypeEU ? 'Eignungsprüfung' : 'Kenntnisprüfung',
            value: isApplicationTypeEU
              ? ActionType.APTITUDE_TEST
              : ActionType.KNOWLEDGE_TEST,
          },
          {
            label: 'Anpassungslehrgang',
            value: ActionType.ADAPTATION_COURSE,
          },
        ]"
        aria-controls="action-date-region"
      />
      <div id="action-date-region" aria-live="polite">
        <IffTransitionHeight>
          <UFormGroup
            v-if="state.action"
            name="actionDate"
            label="Datum der Entscheidung"
            required
            class="max-w-[210px]"
          >
            <template #hint>
              <UButton
                @click="
                  state.actionDate = new Date().toISOString().slice(0, 10)
                "
                icon="i-ic-outline-access-time"
                size="sm"
                color="gray"
                variant="ghost"
                :trailing="false"
                aria-label="Aktuelles Datum wählen"
                title="Aktuelles Datum wählen"
              />
            </template>

            <UInput
              v-model="state.actionDate"
              type="date"
              aria-required="true"
              class="whitespace-normal max-w-[150px]"
            />
          </UFormGroup>
        </IffTransitionHeight>
      </div>
    </IffContentSection>
    <IffContentSection
        headline="Abschließendes Ergebnis der Anpassungsmaßnahme"
        :is-last-child="true"
    >
      <IffRadioGroup
            legend="Abschließendes Ergebnis"
            name="result"
            required
            v-model="state.result"
            :options="[
            { label: 'Liegt noch nicht vor', value: Result.NOT_AVAILABLE },
            {
              label: 'Bestanden',
              value: Result.PASSED,
            },
            {
              label: 'Nicht bestanden',
              value: Result.NOT_PASSED,
            },
            {
              label: 'Abgebrochen ohne endgültiges Ergebnis',
              value: Result.CANCELLED,
            },
          ]"
        />

      <IffTransitionHeight>
        <UFormGroup
            v-if="state.result && state.result !== Result.NOT_AVAILABLE"
            name="resultDate"
            label="Datum der Feststellung"
            required
            class="max-w-[200px]"
        >
          <template #hint>
            <UButton
                @click="state.resultDate = new Date().toISOString().slice(0, 10)"
                icon="i-ic-outline-access-time"
                size="sm"
                color="gray"
                variant="ghost"
                :trailing="false"
                aria-label="Aktuelles Datum wählen"
                title="Aktuelles Datum wählen"
            />
          </template>

          <UInput
              v-model="state.resultDate"
              type="date"
              aria-required="true"
              class="whitespace-normal max-w-[150px]"
          />
        </UFormGroup>
      </IffTransitionHeight>
    </IffContentSection>
  </IffOperationWorkStepFormV2>
</template>
